import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "print-list-page" }
const _hoisted_2 = { class: "page-actions no-print" }
const _hoisted_3 = {
  key: 0,
  class: "print-loading-overlay"
}
const _hoisted_4 = { class: "page-body" }
const _hoisted_5 = { class: "page-header-info" }
const _hoisted_6 = { class: "header-code" }
const _hoisted_7 = { class: "header-date" }
const _hoisted_8 = { class: "company-header" }
const _hoisted_9 = { class: "report-title" }
const _hoisted_10 = { class: "report-code" }
const _hoisted_11 = { class: "info-row" }
const _hoisted_12 = { class: "info-item" }
const _hoisted_13 = { class: "info-item" }
const _hoisted_14 = { class: "info-item" }
const _hoisted_15 = { class: "info-item page-info" }
const _hoisted_16 = { class: "info-value page-value" }
const _hoisted_17 = { class: "info-value page-value" }
const _hoisted_18 = {
  key: 0,
  class: "special-attrs-row"
}
const _hoisted_19 = { class: "attr-label" }
const _hoisted_20 = {
  key: 1,
  class: "continue-page-hint"
}
const _hoisted_21 = {
  key: 2,
  class: "table-container"
}
const _hoisted_22 = { class: "data-table" }
const _hoisted_23 = { key: 0 }
const _hoisted_24 = ["rowspan"]
const _hoisted_25 = ["colspan"]
const _hoisted_26 = { class: "single-attrs-content" }
const _hoisted_27 = { class: "single-attrs-label" }
const _hoisted_28 = ["rowspan"]
const _hoisted_29 = ["rowspan"]
const _hoisted_30 = ["rowspan"]
const _hoisted_31 = { class: "page-footer" }
const _hoisted_32 = { class: "footer-item" }
const _hoisted_33 = {
  key: 0,
  class: "footer-item"
}
const _hoisted_34 = { class: "footer-item" }
const _hoisted_35 = { class: "footer-item" }
const _hoisted_36 = { class: "footer-item date-item" }

import { ref, onMounted, watch, nextTick } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { ElMessage } from 'element-plus'
import { ArrowLeft, Printer, Download, Refresh } from '@element-plus/icons-vue'
import { ParameterSettings } from '@/network/index'
import { useDetailStore } from '@/store/detailStore'
import * as ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'

// 类型定义
interface SpecialAttribute {
  title: string
  value: string
}

interface Section {
  name: string
  columnCount: number
  startIndex: number
}

interface SubRow {
  verticalHeader: string
  cells: string[]
}

interface MainRow {
  buildingFloor: string
  axisNumber: string
  subRows: SubRow[]
  // 各轴线的实际值（用于 @@N 替换）
  axisValues: Record<string, string>
}

// 扁平化的表格行（用于分页计算）
interface FlatTableRow {
  mainIndex: number
  subIndex: number
  isFirstSubRow: boolean
  subRowCount: number
  buildingFloor: string
  axisNumber: string
  verticalHeader: string
  cells: string[]
}

interface TableInfo {
  horizontalHeaders: string[]
  sections: Section[]
  mainRows: MainRow[]
  columnBorders: boolean[]
}

// 分页后的打印数据
interface PrintPageData {
  recordNumber: string
  deviceNumber: string
  temperature: string
  reviewer: string
  recorder: string
  inspector: string
  calculator: string
  showCalculator: boolean
  inspectionDate: string
  implementDate: string
  projectName: string
  controlNumber: string
  componentType: string
  specialAttributes: SpecialAttribute[]
  mainTableSingleAttrs: SpecialAttribute[][]
  tableInfo: TableInfo | null
  // 分页相关
  isFirstPage: boolean
  pageRows: FlatTableRow[]
  sections: Section[]
  // 原始数据索引（用于计算总页数）
  dataIndex: number
  pageIndexInData: number
  totalPagesInData: number
  // 动态行高（用于填充空白区域）
  extraRowHeight: number
  // 每个数据行的动态调整高度，正数用于撑开，负数用于横向同页压缩
  rowExtraHeights: number[]
  // 单页方向（由数据中的方向字段决定）
  isLandscape: boolean
}

// 每页最大行数（根据A4纸高度估算，首页和续页不同）
// 竖向：A4纸高度297mm，减去页眉(约50mm)、页脚(约30mm)、边距(20mm)，表格区域约197mm
// 横向：A4纸高度210mm，减去页眉(约50mm)、页脚(约30mm)、边距(20mm)，表格区域约110mm
// 表头行高约10mm，数据行高约6mm（紧凑模式）

// 竖向模式行数（使用紧凑行高后可容纳更多行）

const __default__ = {
  name: 'printList'
}

export default /*@__PURE__*/_defineComponent({
  ...__default__,
  setup(__props) {

const MAX_ROWS_FIRST_PAGE_PORTRAIT = 23
const MAX_ROWS_CONTINUE_PAGE_PORTRAIT = 25

// 横向模式行数
const MAX_ROWS_FIRST_PAGE_LANDSCAPE = 23
const MAX_ROWS_CONTINUE_PAGE_LANDSCAPE = 25

// 基础行高（单位：px，竖向和横向统一使用紧凑行高）
const BASE_ROW_HEIGHT = 16
const LANDSCAPE_BASE_ROW_HEIGHT = 16
// 横向空间不足时允许在同页压缩行高，避免末行被固定高度裁掉。
const LANDSCAPE_MIN_ROW_HEIGHT = 14
const SINGLE_ATTR_ROW_WEIGHT = 0.35
// 动态撑开时保留安全余量，横向打印额外预留空间，避免像素取整裁掉末行和底部边框。
const PAGE_FILL_SAFETY_PX = 3
const LANDSCAPE_PAGE_FILL_SAFETY_PX = 10

const router = useRouter()
const route = useRoute()
const detailStore = useDetailStore()

// 根据打印方向获取每页最大行数
const getMaxRowsFirstPage = (isLandscape: boolean) => isLandscape ? MAX_ROWS_FIRST_PAGE_LANDSCAPE : MAX_ROWS_FIRST_PAGE_PORTRAIT
const getMaxRowsContinuePage = (isLandscape: boolean) => isLandscape ? MAX_ROWS_CONTINUE_PAGE_LANDSCAPE : MAX_ROWS_CONTINUE_PAGE_PORTRAIT

const getSingleAttrRowWeight = (attrs?: SpecialAttribute[]): number => {
  if (!attrs || attrs.length === 0) return 0
  return Math.ceil(attrs.length / 4) * SINGLE_ATTR_ROW_WEIGHT
}

const loading = ref(false)
const printContentRef = ref<HTMLElement | null>(null)
const printData = ref<PrintPageData[]>([])
let fillMeasureToken = 0

// 计算行高样式（应用到tr上）
const getRowStyle = (extraRowHeight: number, isLandscape: boolean): Record<string, string> => {
  const baseRowHeight = isLandscape ? LANDSCAPE_BASE_ROW_HEIGHT : BASE_ROW_HEIGHT
  const minRowHeight = isLandscape ? LANDSCAPE_MIN_ROW_HEIGHT : BASE_ROW_HEIGHT
  const totalHeight = Math.max(baseRowHeight + extraRowHeight, minRowHeight)
  return {
    height: `${totalHeight}px`
  }
}

// 计算单元格高度样式（应用到td上，确保高度生效）
const getCellStyle = (extraRowHeight: number, isLandscape: boolean): Record<string, string> => {
  const baseRowHeight = isLandscape ? LANDSCAPE_BASE_ROW_HEIGHT : BASE_ROW_HEIGHT
  const minRowHeight = isLandscape ? LANDSCAPE_MIN_ROW_HEIGHT : BASE_ROW_HEIGHT
  const totalHeight = Math.max(baseRowHeight + extraRowHeight, minRowHeight)
  if (extraRowHeight !== 0) {
    return {
      height: `${totalHeight}px`,
      minHeight: `${totalHeight}px`,
      boxSizing: 'border-box'
    }
  }
  return {}
}

const waitForFrame = (): Promise<void> => new Promise(resolve => {
  requestAnimationFrame(() => resolve())
})

const waitForLayoutStable = async () => {
  await nextTick()
  await waitForFrame()
  await waitForFrame()
  if (document.fonts?.ready) {
    try {
      await document.fonts.ready
    } catch {
      // ignore font loading errors and continue with best available layout
    }
  }
}

const applyMeasuredPageFill = async () => {
  if (!printData.value.length) return

  const token = ++fillMeasureToken

  // 第一步：重置所有填充，以紧凑模式渲染
  let pages = printData.value.map(page => ({
    ...page,
    extraRowHeight: 0,
    rowExtraHeights: page.pageRows.map(() => 0)
  }))

  // 多轮测量是必要的：表格的 border-collapse 和 rowspan 会让实际高度
  // 与 CSS 设置值存在细小差异，需要逐轮正向撑开或反向压缩。
  for (let round = 0; round < 4; round++) {
    printData.value = pages
    await waitForLayoutStable()

    if (token !== fillMeasureToken) return

    const pageEls = Array.from(
      printContentRef.value?.querySelectorAll<HTMLElement>('.print-content') || []
    )
    let changed = false

    pages = pages.map((page, pageIndex) => {
      const pageEl = pageEls[pageIndex]
      const tableEl = pageEl?.querySelector<HTMLElement>('.data-table')
      const footerEl = pageEl?.querySelector<HTMLElement>('.page-footer')

      if (!tableEl || !footerEl || page.pageRows.length === 0) {
        return page
      }

      // 直接测量表格底部到页脚顶部的间距，避免通过页面高度反推时
      // 把 flex 容器的内部空余高度重复计算进去。
      const tableRect = tableEl.getBoundingClientRect()
      const footerRect = footerEl.getBoundingClientRect()
      const fillSafety = page.isLandscape ? LANDSCAPE_PAGE_FILL_SAFETY_PX : PAGE_FILL_SAFETY_PX
      const remainingGap = Math.floor(footerRect.top - tableRect.bottom - fillSafety)

      const rowCount = page.pageRows.length
      const currentHeights = page.rowExtraHeights?.length === rowCount
        ? page.rowExtraHeights
        : page.pageRows.map(() => 0)
      let rowExtraHeights: number[]

      if (remainingGap > 0) {
        const fillPerRow = Math.floor(remainingGap / rowCount)
        const remainder = remainingGap % rowCount
        rowExtraHeights = currentHeights.map((height, index) =>
          height + fillPerRow + (index < remainder ? 1 : 0)
        )
      } else if (remainingGap < 0 && page.isLandscape) {
        // 横向页超出安全区时反向压缩，优先在同一页完整保留末行和底部边框。
        const minExtraHeight = LANDSCAPE_MIN_ROW_HEIGHT - LANDSCAPE_BASE_ROW_HEIGHT
        const reducibleHeight = currentHeights.reduce(
          (total, height) => total + Math.max(0, height - minExtraHeight),
          0
        )
        const reduction = Math.min(-remainingGap, reducibleHeight)
        const reducePerRow = Math.floor(reduction / rowCount)
        const remainder = reduction % rowCount
        rowExtraHeights = currentHeights.map((height, index) =>
          Math.max(minExtraHeight, height - reducePerRow - (index < remainder ? 1 : 0))
        )
      } else {
        return page
      }

      const hasChanged = rowExtraHeights.some((height, index) => height !== currentHeights[index])
      if (!hasChanged) {
        return page
      }

      changed = true
      return {
        ...page,
        extraRowHeight: Math.max(...rowExtraHeights, 0),
        rowExtraHeights
      }
    })

    if (!changed) break
  }

  if (token !== fillMeasureToken) return

  printData.value = pages
  await waitForLayoutStable()

  // 重新获取应用动态行高后的节点，避免使用填充前的旧 DOM 尺寸进行验证。
  const finalPageEls = Array.from(
    printContentRef.value?.querySelectorAll<HTMLElement>('.print-content') || []
  )
  pages.forEach((page, pageIndex) => {
    const pageEl = finalPageEls[pageIndex]
    const tableEl = pageEl?.querySelector<HTMLElement>('.data-table')
    const footerEl = pageEl?.querySelector<HTMLElement>('.page-footer')

    if (!tableEl || !footerEl) return

    const finalGap = footerEl.getBoundingClientRect().top - tableEl.getBoundingClientRect().bottom
    if (finalGap < -1) {
      console.error(`[页面${pageIndex + 1}] 行高调整后仍溢出 ${Math.abs(finalGap).toFixed(0)}px`)
    }
  })
}

const hasDisplayValue = (value: unknown): boolean => {
  if (value === null || value === undefined) {
    return false
  }
  if (typeof value === 'string') {
    return value.trim().length > 0
  }
  return true
}

// 加载打印数据
const loadPrintData = async () => {
  loading.value = true
  try {
    const cacheKey = typeof route.query.cacheKey === 'string' ? route.query.cacheKey : ''
    const rawData = cacheKey
      ? detailStore.getPrintListData(cacheKey)
      : detailStore.getLatestPrintListData()

    if (rawData && rawData.length > 0) {
      // 使用新的渲染式分页
      const allPages: PrintPageData[] = []
      for (const item of rawData) {
        const pages = await parsePrintItemWithRenderPagination(item, allPages.length)
        allPages.push(...pages)
      }
      printData.value = allPages
      await applyMeasuredPageFill()
    } else {
      printData.value = []
      ElMessage.warning('暂无可用的打印数据，请重新获取')
    }
  } finally {
    loading.value = false
  }
}

// 新的渲染式分页：先渲染后分页
const parsePrintItemWithRenderPagination = async (item: {
  方向: boolean;
  记录编号: string;
  设备编号: string;
  环境温度: string;
  校核: string;
  记录: string;
  检验: string;
  计算: string;
  检验日期: string;
  检测原始数据检测项目: Record<string, unknown>;
}, dataIndexOffset: number): Promise<PrintPageData[]> => {
  const project = item.检测原始数据检测项目 || {}
  const formatDesc = (project.检测原始数据检测数据格式描述 as Record<string, unknown>) || {}

  const isLandscape = item.方向 === true

  const detailDataList =
    (project.检测原始数据详细数据主表集合 as Array<Record<string, unknown>>) || []
  const mainTableSingleAttrs: SpecialAttribute[][] = detailDataList.map((detailData) =>
    parseSpecialAttributes(
      (detailData.检测原始数据检测项目单条数据特殊属性集合 as Array<Record<string, unknown>>) || []
    )
  )

  const baseData = {
    recordNumber: item.记录编号 || '',
    deviceNumber: item.设备编号 || '',
    temperature: item.环境温度 || '',
    reviewer: item.校核 || '',
    recorder: item.记录 || '',
    inspector: item.检验 || '',
    calculator: item.计算 || '',
    showCalculator: Boolean(item.计算) || Boolean(project.是否计算),
    inspectionDate: item.检验日期 || '',
    implementDate: (project.实施日期 as string) || '',
    projectName: (project.名称 as string) || '',
    controlNumber: (project.受控编号 as string) || '',
    componentType: (project.检测对象类型 as string) || '',
    specialAttributes: parseSpecialAttributes(
      (project.检测原始数据检测项目特殊属性集合 as Array<Record<string, unknown>>) || []
    ),
    mainTableSingleAttrs,
    dataIndex: dataIndexOffset,
    isLandscape
  }

  const tableInfo = parseTableInfo(formatDesc, project)

  if (!tableInfo || tableInfo.mainRows.length === 0) {
    return [{
      ...baseData,
      tableInfo,
      isFirstPage: true,
      pageRows: [],
      sections: tableInfo?.sections || [],
      pageIndexInData: 0,
      totalPagesInData: 1,
      extraRowHeight: 0,
      rowExtraHeights: []
    }]
  }

  // 将表格数据扁平化为行
  const flatRows: FlatTableRow[] = []
  tableInfo.mainRows.forEach((mainRow, mainIndex) => {
    const singleAttrs = mainTableSingleAttrs[mainIndex] || []
    const subRows = mainRow.subRows.length > 0
      ? mainRow.subRows
      : singleAttrs.length > 0
        ? [{ verticalHeader: '', cells: tableInfo.horizontalHeaders.map(() => '') }]
        : []

    subRows.forEach((subRow, subIndex) => {
      flatRows.push({
        mainIndex,
        subIndex,
        isFirstSubRow: subIndex === 0,
        subRowCount: subRows.length,
        buildingFloor: mainRow.buildingFloor,
        axisNumber: mainRow.axisNumber,
        verticalHeader: subRow.verticalHeader,
        cells: subRow.cells
      })
    })
  })

  console.log(`[渲染式分页] 开始处理，共${flatRows.length}行数据，方向: ${isLandscape ? '横向' : '竖向'}`)

  // 渲染式分页：逐步添加行并测量
  const pages: PrintPageData[] = []
  let currentPageRows: FlatTableRow[] = []
  let currentIndex = 0
  let pageIndexInData = 0

  while (currentIndex < flatRows.length) {
    const currentRow = flatRows[currentIndex]
    const isFirstPage = pageIndexInData === 0

    // 尝试添加这个主表的所有行
    if (currentRow.isFirstSubRow) {
      const tempRows = [...currentPageRows]
      const mainStartIndex = currentIndex
      const mainEndIndex = currentIndex + currentRow.subRowCount

      // 收集主表的所有子行
      for (let j = mainStartIndex; j < mainEndIndex && j < flatRows.length; j++) {
        tempRows.push(flatRows[j])
      }

      // 创建临时页面数据进行测量
      const tempPage: PrintPageData = {
        ...baseData,
        tableInfo,
        isFirstPage,
        pageRows: tempRows,
        sections: tableInfo.sections,
        pageIndexInData,
        totalPagesInData: 0,
        extraRowHeight: 0,
        rowExtraHeights: tempRows.map(() => 0),
        isLandscape
      }

      // 渲染并测量是否溢出
      printData.value = [tempPage]
      await waitForLayoutStable()

      let isOverflow = await checkPageOverflow(0)

      if (isOverflow && isLandscape) {
        // 分页前先按最低行高实测，能同页容纳时不把主表移到下一页。
        printData.value = [{
          ...tempPage,
          rowExtraHeights: tempRows.map(() => LANDSCAPE_MIN_ROW_HEIGHT - LANDSCAPE_BASE_ROW_HEIGHT)
        }]
        await waitForLayoutStable()
        isOverflow = await checkPageOverflow(0)
      }

      if (isOverflow && currentPageRows.length > 0) {
        // 溢出且当前页已有内容 -> 完成当前页，主表放到下一页
        console.log(`[渲染式分页] 页面${pageIndexInData + 1}完成，共${currentPageRows.length}行`)
        pages.push({
          ...baseData,
          tableInfo,
          isFirstPage,
          pageRows: currentPageRows,
          sections: tableInfo.sections,
          pageIndexInData,
          totalPagesInData: 0,
          extraRowHeight: 0,
          rowExtraHeights: currentPageRows.map(() => 0),
          isLandscape
        })

        // 开始新页
        currentPageRows = []
        pageIndexInData++
      } else {
        // 不溢出，或者当前页为空（强制放入）-> 接受这个主表
        currentPageRows = tempRows
        currentIndex = mainEndIndex
      }
    } else {
      // 不应该出现
      console.warn(`[渲染式分页] 遇到非首行`)
      currentIndex++
    }
  }

  // 添加最后一页
  if (currentPageRows.length > 0) {
    console.log(`[渲染式分页] 页面${pageIndexInData + 1}完成（最后一页），共${currentPageRows.length}行`)
    pages.push({
      ...baseData,
      tableInfo,
      isFirstPage: pageIndexInData === 0,
      pageRows: currentPageRows,
      sections: tableInfo.sections,
      pageIndexInData,
      totalPagesInData: 0,
      extraRowHeight: 0,
      rowExtraHeights: currentPageRows.map(() => 0),
      isLandscape
    })
  }

  // 更新总页数
  pages.forEach(page => {
    page.totalPagesInData = pages.length
  })

  console.log(`[渲染式分页] 完成，共${pages.length}页`)

  return pages
}

// 检查页面是否溢出
const checkPageOverflow = async (pageIndex: number): Promise<boolean> => {
  const pageEls = Array.from(
    printContentRef.value?.querySelectorAll<HTMLElement>('.print-content') || []
  )

  const pageEl = pageEls[pageIndex]
  if (!pageEl) return false

  const tableContainerEl = pageEl.querySelector<HTMLElement>('.table-container')
  const footerEl = pageEl.querySelector<HTMLElement>('.page-footer')

  if (!tableContainerEl || !footerEl) return false

  const pageRect = pageEl.getBoundingClientRect()
  const tableContainerRect = tableContainerEl.getBoundingClientRect()
  const footerRect = footerEl.getBoundingClientRect()

  const headerContentHeight = tableContainerRect.top - pageRect.top
  const footerHeight = pageRect.bottom - footerRect.top
  const tableAvailableHeight = pageRect.height - headerContentHeight - footerHeight

  const tableEl = pageEl.querySelector<HTMLElement>('.data-table')
  if (!tableEl) return false

  const tableRect = tableEl.getBoundingClientRect()
  const currentTableHeight = tableRect.height

  // 只留10px安全边距
  const isOverflow = currentTableHeight > (tableAvailableHeight - 10)

  if (isOverflow) {
    console.log(`[溢出检测] 页面${pageIndex + 1}: 表格${currentTableHeight.toFixed(0)}px > 可用${(tableAvailableHeight - 10).toFixed(0)}px`)
  }

  return isOverflow
}

// 解析单条打印数据并分页（旧方法，保留作为备用）
const parsePrintItemWithPagination = (item: {
  方向: boolean;
  记录编号: string;
  设备编号: string;
  环境温度: string;
  校核: string;
  记录: string;
  检验: string;
  计算: string;
  检验日期: string;
  检测原始数据检测项目: Record<string, unknown>;
}, dataIndex: number): PrintPageData[] => {
  const project = item.检测原始数据检测项目 || {}
  const formatDesc = (project.检测原始数据检测数据格式描述 as Record<string, unknown>) || {}
  
  // 从项目顶层获取方向字段：true=横向，false=竖向
  const isLandscape = item.方向 === true

  const detailDataList =
    (project.检测原始数据详细数据主表集合 as Array<Record<string, unknown>>) || []
  const mainTableSingleAttrs: SpecialAttribute[][] = detailDataList.map((detailData) =>
    parseSpecialAttributes(
      (detailData.检测原始数据检测项目单条数据特殊属性集合 as Array<Record<string, unknown>>) || []
    )
  )

  const baseData = {
    recordNumber: item.记录编号 || '',
    deviceNumber: item.设备编号 || '',
    temperature: item.环境温度 || '',
    reviewer: item.校核 || '',
    recorder: item.记录 || '',
    inspector: item.检验 || '',
    calculator: item.计算 || '',
    showCalculator: Boolean(item.计算) || Boolean(project.是否计算),
    inspectionDate: item.检验日期 || '',
    implementDate: (project.实施日期 as string) || '',
    projectName: (project.名称 as string) || '',
    controlNumber: (project.受控编号 as string) || '',
    componentType: (project.检测对象类型 as string) || '',
    specialAttributes: parseSpecialAttributes(
      (project.检测原始数据检测项目特殊属性集合 as Array<Record<string, unknown>>) || []
    ),
    mainTableSingleAttrs,
    dataIndex,
    isLandscape
  }

  const tableInfo = parseTableInfo(formatDesc, project)
  
  if (!tableInfo || tableInfo.mainRows.length === 0) {
    // 没有表格数据，返回单页
    return [{
      ...baseData,
      tableInfo,
      isFirstPage: true,
      pageRows: [],
      sections: tableInfo?.sections || [],
      pageIndexInData: 0,
      totalPagesInData: 1,
      extraRowHeight: 0,
      rowExtraHeights: []
    }]
  }

  // 将表格数据扁平化为行
  const flatRows: FlatTableRow[] = []
  tableInfo.mainRows.forEach((mainRow, mainIndex) => {
    const singleAttrs = mainTableSingleAttrs[mainIndex] || []
    const subRows = mainRow.subRows.length > 0
      ? mainRow.subRows
      : singleAttrs.length > 0
        ? [{ verticalHeader: '', cells: tableInfo.horizontalHeaders.map(() => '') }]
        : []

    subRows.forEach((subRow, subIndex) => {
      flatRows.push({
        mainIndex,
        subIndex,
        isFirstSubRow: subIndex === 0,
        subRowCount: subRows.length,
        buildingFloor: mainRow.buildingFloor,
        axisNumber: mainRow.axisNumber,
        verticalHeader: subRow.verticalHeader,
        cells: subRow.cells
      })
    })
  })

  // 按行数分页，确保主表不被截断
  // 动态计算表头占用的等效行数（考虑列数多时表头可能换行导致高度增加）
  const calculateHeaderRowEquivalent = (horizontalHeaders: string[], isLandscape: boolean): number => {
    const colCount = horizontalHeaders.length

    let headerRowEquivalent = 1

    // 根据列数和打印方向调整
    if (isLandscape) {
      // 横向打印：纸张高度有限，但宽度充足，表头换行相对较少
      if (colCount > 25) {
        headerRowEquivalent = 2.5    // 列数很多，表头很高
      } else if (colCount > 20) {
        headerRowEquivalent = 2
      } else if (colCount > 15) {
        headerRowEquivalent = 1.8
      } else if (colCount > 10) {
        headerRowEquivalent = 1.5
      } else if (colCount > 5) {
        headerRowEquivalent = 1.3
      } else {
        headerRowEquivalent = 1
      }
    } else {
      // 竖向打印：纸张宽度有限，表头换行较多
      if (colCount > 25) {
        headerRowEquivalent = 2
      } else if (colCount > 20) {
        headerRowEquivalent = 1.8
      } else if (colCount > 15) {
        headerRowEquivalent = 1.6
      } else if (colCount > 10) {
        headerRowEquivalent = 1.4
      } else if (colCount > 5) {
        headerRowEquivalent = 1.2
      } else {
        headerRowEquivalent = 1
      }
    }

    // 调试日志
    console.log(`[分页计算] 列数: ${colCount}, 方向: ${isLandscape ? '横向' : '竖向'}, 表头等效行数: ${headerRowEquivalent.toFixed(1)}`)

    return headerRowEquivalent
  }

  const HEADER_ROW_EQUIVALENT = calculateHeaderRowEquivalent(tableInfo.horizontalHeaders, isLandscape)
  const pages: PrintPageData[] = []
  let currentIndex = 0
  let pageIndexInData = 0

  while (currentIndex < flatRows.length) {
    const isFirstPage = pageIndexInData === 0
    const maxRows = isFirstPage ? getMaxRowsFirstPage(isLandscape) : getMaxRowsContinuePage(isLandscape)
    // 实际可用的数据行数（减去表头等效行数）
    const availableRows = Math.max(1, Math.floor(maxRows - HEADER_ROW_EQUIVALENT))

    // 调试日志
    if (currentIndex === 0) {
      console.log(`[分页计算] 每页最大行数: ${maxRows}, 表头等效: ${HEADER_ROW_EQUIVALENT.toFixed(1)}, 可用数据行: ${availableRows}`)
    }
    
    const pageRows: FlatTableRow[] = []
    let rowsInPage = 0
    let i = currentIndex

    while (i < flatRows.length) {
      const currentRow = flatRows[i]

      // 如果是主表的第一行，需要检查整个主表
      if (currentRow.isFirstSubRow) {
        const singleAttrWeight = getSingleAttrRowWeight(mainTableSingleAttrs[currentRow.mainIndex])
        const mainRowCount = currentRow.subRowCount + singleAttrWeight
        const remainingSpace = availableRows - rowsInPage

        // 如果整个主表放不下
        if (mainRowCount > remainingSpace) {
          // 如果当前页已经有超过50%的内容，断页，将主表放到下一页
          if (rowsInPage > Math.floor(availableRows * 0.5)) {
            break
          }
          // 否则，即使超出也强制放入（避免页面太空）
        }

        // 将整个主表的所有子行一次性添加
        const mainStartIndex = i
        const mainEndIndex = i + currentRow.subRowCount

        for (let j = mainStartIndex; j < mainEndIndex && j < flatRows.length; j++) {
          pageRows.push(flatRows[j])
        }

        rowsInPage += mainRowCount
        i = mainEndIndex

        // 添加调试日志
        console.log(`[分页] 主表${currentRow.mainIndex + 1}: ${currentRow.subRowCount}子行 + ${singleAttrWeight}特殊属性 = ${mainRowCount}行, 当前页累计: ${rowsInPage}/${availableRows}`)
      } else {
        // 理论上不应该走到这里，因为所有行都应该有isFirstSubRow标记
        console.warn(`[分页警告] 遇到非首行: mainIndex=${currentRow.mainIndex}, subIndex=${currentRow.subIndex}`)
        pageRows.push(currentRow)
        rowsInPage++
        i++
      }

      // 安全终止：如果累计行数已经超过可用行数，停止添加
      if (rowsInPage >= availableRows) {
        console.log(`[分页] 页面已满 (${rowsInPage}/${availableRows})，结束当前页`)
        break
      }
    }
    
    pages.push({
      ...baseData,
      tableInfo,
      isFirstPage,
      pageRows,
      sections: tableInfo.sections,
      pageIndexInData,
      totalPagesInData: 0, // 稍后计算
      extraRowHeight: 0,
      rowExtraHeights: pageRows.map(() => 0),
      isLandscape
    })

    currentIndex = i
    pageIndexInData++
  }

  // 更新总页数；行高填充在 DOM 渲染后按实际剩余空间计算。
  pages.forEach(page => {
    page.totalPagesInData = pages.length
  })

  return pages
}

// 解析特殊属性
const parseSpecialAttributes = (attrList: Array<Record<string, unknown>>): SpecialAttribute[] => {
  if (!attrList || !Array.isArray(attrList)) return []
  
  // 排除已在表头显示的属性
  const excludeAttrs = ['环境温度', '设备编号']
  
  return attrList
    .filter(attr => {
      const title = (attr.标题 as string) || ''
      return title && !excludeAttrs.includes(title)
    })
    .map(attr => ({
      title: (attr.标题 as string) || '',
      value: (attr.内容 as string) || ''
    }))
}

// 解析表格信息
const parseTableInfo = (
  formatDesc: Record<string, unknown>,
  project: Record<string, unknown>
): TableInfo | null => {
  const horizontalHeaders = (formatDesc.横向标题 as string[]) || []
  const columnBorders = (formatDesc.分区线 as boolean[]) || []

  if (horizontalHeaders.length === 0) return null

  // 解析分区标题
  let sectionNames: string[] = []
  try {
    const sectionTitleJSON = formatDesc.分区标题JSON as string
    if (sectionTitleJSON) {
      sectionNames = JSON.parse(sectionTitleJSON)
    }
  } catch {
    sectionNames = []
  }

  // 构建分区信息
  const sections = parseSections(horizontalHeaders, columnBorders, sectionNames)

  // 获取详细数据主表集合 - 每个主表对应一个大行
  const detailDataList =
    (project.检测原始数据详细数据主表集合 as Array<Record<string, unknown>>) || []

  // 构建主行数据
  const mainRows: MainRow[] = detailDataList.map(
    (detailData: Record<string, unknown>) => {
      // 楼号楼层：建筑名称 + 层数 + "层"，如 "清芬1层"
      const floor = (detailData.层数 as string | number) || ''
      const buildingName = (detailData.建筑名称 as string) || ''
      const buildingFloor = buildingName
        ? `${buildingName}${floor}层`
        : floor
          ? `${floor}层`
          : ''

      // 轴线号：从显示摘要中提取 "轴线：[xxx]"
      const summary = (detailData.显示摘要 as string) || ''
      let axisNumber = ''
      const axisMatch = summary.match(/轴线[：:]\s*\[([^\]]*)\]/)
      if (axisMatch) {
        axisNumber = axisMatch[1]
      }

      // 各轴线值（用于 @@N 替换）：保留原始 轴线1~轴线4 字段
      const axisValues: Record<string, string> = {
        '1': String((detailData['轴线1'] as string) ?? ''),
        '2': String((detailData['轴线2'] as string) ?? ''),
        '3': String((detailData['轴线3'] as string) ?? ''),
        '4': String((detailData['轴线4'] as string) ?? '')
      }

      // 获取子表数据 - 每个子表对应一个小行
      const subDataList =
        (detailData.检测原始数据详细数据子表集合 as Array<Record<string, unknown>>) || []

      const subRows: SubRow[] = subDataList.map(
        (subData: Record<string, unknown>) => {
          // 纵向标题（去掉@@前缀）
          let verticalHeader = (subData.Header as string) || ''
          if (verticalHeader.startsWith('@@')) {
            verticalHeader = verticalHeader.substring(2)
          }

          // 构建单元格数据
          const cells: string[] = horizontalHeaders.map(
            (_: string, colIndex: number) => {
              const key = `_${colIndex + 1}`
              const value = subData[key]
              if (value === undefined || value === null || value === '') {
                return ''
              }
              // 布尔值转换：true -> 是，false -> 否
              if (value === true || value === 'true' || value === 'True') {
                return '是'
              } else if (value === false || value === 'false' || value === 'False') {
                return '否'
              }
              return String(value)
            }
          )

          return {
            verticalHeader,
            cells
          }
        }
      )

      return {
        buildingFloor,
        axisNumber,
        subRows,
        axisValues
      }
    }
  )

  return {
    horizontalHeaders,
    sections,
    mainRows,
    columnBorders
  }
}

const parseSections = (
  horizontalHeaders: string[],
  columnBorders: boolean[],
  sectionNames: string[]
): Section[] => {
  const sections: Section[] = []

  if (horizontalHeaders.length === 0) return sections

  // 判断数组格式：
  // - 如果长度 > 横向标题数量，第一个元素是第一分区名称
  // - 如果长度 = 横向标题数量，没有第一分区名称（旧格式兼容）
  const hasFirstSectionSlot = sectionNames.length > horizontalHeaders.length

  let currentStart = 0
  // 第一个分区的名称
  let currentSectionName = hasFirstSectionSlot ? (sectionNames[0] || '') : ''

  for (let i = 0; i < horizontalHeaders.length; i++) {
    // 检查是否需要结束当前分区（在分区线位置或最后一列）
    const isLastColumn = i === horizontalHeaders.length - 1
    const hasBorder = columnBorders[i] === true

    if (hasBorder || isLastColumn) {
      const columnCount = i - currentStart + 1
      sections.push({
        name: currentSectionName,
        columnCount,
        startIndex: currentStart
      })

      // 准备下一个分区
      if (!isLastColumn) {
        currentStart = i + 1
        // 下一个分区的名称：sectionNames[i+1] 是第 i 列右边分区线后的分区名称
        // 如果有第一分区槽位，索引为 i+1；否则为 i（旧格式兼容）
        const nextNameIndex = hasFirstSectionSlot ? i + 1 : i
        currentSectionName = sectionNames[nextNameIndex] || ''
      }
    }
  }

  return sections
}

// 获取单元格的分区样式类（基于 sections 数组）
const getSectionClassByIndex = (sections: Section[], colIndex: number): string => {
  for (const section of sections) {
    if (section.startIndex === colIndex && colIndex > 0) {
      return 'section-start'
    }
  }
  return ''
}

// 判断当前行是否是该主表在当前页的第一行
const isFirstRowOfMainInPage = (pageRows: FlatTableRow[], rowIndex: number): boolean => {
  const currentRow = pageRows[rowIndex]
  if (rowIndex === 0) return true
  const prevRow = pageRows[rowIndex - 1]
  return prevRow.mainIndex !== currentRow.mainIndex
}

// 转换横向标题：处理 @@1~4 替换为对应轴线值（与详情页逻辑一致）
const getTransformedTitle = (title: string, tableInfo: TableInfo | null, firstRow?: FlatTableRow): string => {
  const match = title.match(/@@([1-4])/)
  if (!match) return title
  const axisNumber = match[1]

  // 优先从对应主表的 axisValues 取（来自原始主表的 轴线1~轴线4）
  const mainRow = firstRow ? tableInfo?.mainRows?.[firstRow.mainIndex] : undefined
  const axisValue = mainRow?.axisValues?.[axisNumber] || ''

  // 如果轴线值为空或 "0"，保留原标题
  if (!axisValue || axisValue === '0') {
    return title
  }
  return title.replace(/@@([1-4])/, axisValue)
}

// 判断横向标题对应列是否有分区线
const hasDividerLine = (tableInfo: TableInfo | null, index: number): boolean => {
  if (!tableInfo || !Array.isArray(tableInfo.columnBorders)) return false
  return tableInfo.columnBorders[index] === true
}

const getTableColumnCount = (page: PrintPageData): number => {
  return 4 + (page.tableInfo?.horizontalHeaders?.length || 0)
}

// 获取指定主表的单条数据特殊属性列表
const getMainSingleAttrsList = (page: PrintPageData, mainIndex: number): SpecialAttribute[] => {
  return page.mainTableSingleAttrs?.[mainIndex] || []
}

// 需要显示的项目级特殊属性：过滤掉标题为"检测对象"且值为"默认"的项
const visibleSpecialAttributes = (page: PrintPageData): SpecialAttribute[] => {
  return (page.specialAttributes || []).filter((attr) => {
    if (attr.title === '检测对象' && String(attr.value ?? '').trim() === '默认') {
      return false
    }
    return true
  })
}

const hasMainSingleAttrs = (page: PrintPageData, mainIndex: number): boolean => {
  return getMainSingleAttrsList(page, mainIndex).length > 0
}

const shouldRenderSingleAttrsRow = (page: PrintPageData, row: FlatTableRow, rowIndex: number): boolean => {
  return row.isFirstSubRow && isFirstRowOfMainInPage(page.pageRows, rowIndex) && hasMainSingleAttrs(page, row.mainIndex)
}

const shouldRenderSeqCell = (page: PrintPageData, row: FlatTableRow, rowIndex: number): boolean => {
  return row.isFirstSubRow && isFirstRowOfMainInPage(page.pageRows, rowIndex) && !hasMainSingleAttrs(page, row.mainIndex)
}

const getSeqRowspanInPage = (page: PrintPageData, rowIndex: number): number => {
  const row = page.pageRows[rowIndex]
  return getRowspanInPage(page.pageRows, rowIndex) + (hasMainSingleAttrs(page, row.mainIndex) ? 1 : 0)
}

const formatSingleAttrsText = (attrs: SpecialAttribute[]): string => {
  return attrs.map(attr => `${attr.title}：${attr.value}`).join('  ')
}

// 计算当前主表在当前页的 rowspan
const getRowspanInPage = (pageRows: FlatTableRow[], rowIndex: number): number => {
  const currentRow = pageRows[rowIndex]
  let count = 1
  for (let i = rowIndex + 1; i < pageRows.length; i++) {
    if (pageRows[i].mainIndex === currentRow.mainIndex) {
      count++
    } else {
      break
    }
  }
  return count
}

// 格式化日期代码（如 20250711）
const formatDateCode = (dateStr: string): string => {
  if (!dateStr || dateStr.startsWith('0001') || dateStr.startsWith('1900')) return ''
  try {
    const date = new Date(dateStr)
    if (isNaN(date.getTime())) return ''
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}${month}${day}`
  } catch {
    return ''
  }
}

// 格式化日期（如 2025年7月11日）
const formatDate = (dateStr: string): string => {
  if (!dateStr || dateStr.startsWith('0001') || dateStr.startsWith('1900')) {
    return '____年__月__日'
  }
  try {
    const date = new Date(dateStr)
    if (isNaN(date.getTime())) return '____年__月__日'
    return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`
  } catch {
    return '____年__月__日'
  }
}

// 返回上一页
const goBack = () => {
  router.go(-1)
}

const handleRefreshPrintData = async () => {
  const projectId = typeof route.query.id === 'string' ? route.query.id : ''
  if (!projectId) {
    ElMessage.warning('缺少项目ID，无法刷新打印数据')
    return
  }

  loading.value = true
  try {
    const res = await ParameterSettings.operatingProjectPrint({ id: projectId })
    if (!res?.success || !Array.isArray(res.response)) {
      ElMessage.error(res?.msg || '刷新打印数据失败')
      return
    }

    const cacheKey = typeof route.query.cacheKey === 'string' && route.query.cacheKey
      ? route.query.cacheKey
      : `analysis-print-${projectId}`
    detailStore.setPrintListData(cacheKey, res.response)
    await loadPrintData()
    ElMessage.success('打印数据已刷新')
  } catch (error) {
    console.error('刷新打印数据失败:', error)
    ElMessage.error('刷新打印数据失败')
  } finally {
    loading.value = false
  }
}

// 打印功能 - 支持混合方向
const handlePrint = async () => {
  await applyMeasuredPageFill()
  await nextTick()

  const printContent = printContentRef.value
  if (!printContent) {
    ElMessage.warning('打印内容未加载')
    return
  }

  // 检查是否有混合方向
  const hasLandscape = printData.value.some(p => p.isLandscape)
  const hasPortrait = printData.value.some(p => !p.isLandscape)
  const isMixedOrientation = hasLandscape && hasPortrait
  
  // 混合方向默认使用竖向纸张，横向内容保持90度旋转展示
  const pageOrientation = hasLandscape && !hasPortrait ? 'landscape' : 'portrait'

  // 创建隐藏的iframe
  const iframe = document.createElement('iframe')
  iframe.style.position = 'absolute'
  iframe.style.width = '0'
  iframe.style.height = '0'
  iframe.style.border = 'none'
  iframe.style.left = '-9999px'
  document.body.appendChild(iframe)

  const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document
  if (!iframeDoc) {
    document.body.removeChild(iframe)
    ElMessage.error('打印初始化失败')
    return
  }

  // 获取当前页面的样式
  const styles = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
    .map((el) => el.outerHTML)
    .join('')

  // 只调整打印克隆：每个旋转页使用独立的竖向分页容器。
  const printContentClone = printContent.cloneNode(true) as HTMLElement
  if (isMixedOrientation) {
    const landscapePages = Array.from(
      printContentClone.querySelectorAll<HTMLElement>('.print-content.landscape-content')
    )
    landscapePages.forEach(pageEl => {
      const parent = pageEl.parentNode
      if (!parent) return

      const shell = document.createElement('div')
      shell.className = 'landscape-print-shell'
      parent.replaceChild(shell, pageEl)
      shell.appendChild(pageEl)
    })
  }

  const landscapeRotateStyle = isMixedOrientation ? `
    /* 混合方向统一使用默认竖向 @page，避免命名页重复切换纸张 */
    .print-content,
    .print-content.landscape-content,
    .landscape-print-shell {
      page: auto !important;
    }

    .print-content:not(.landscape-content) {
      transform: none !important;
    }

    /* 外壳参与分页，内部内容保留文档流并旋转，避免连续页重叠 */
    .landscape-print-shell {
      position: relative;
      width: 210mm !important;
      height: 297mm !important;
      min-height: 297mm !important;
      max-height: 297mm !important;
      margin: 0 auto !important;
      overflow: hidden;
      box-sizing: border-box;
      background: #fff;
      page-break-after: always;
      page-break-inside: avoid;
    }

    .landscape-print-shell:last-child {
      page-break-after: avoid;
    }

    .landscape-print-shell > .print-content.landscape-content {
      position: relative !important;
      left: -43.5mm !important;
      top: 43.5mm !important;
      width: 297mm !important;
      height: 210mm !important;
      min-height: 210mm !important;
      max-height: 210mm !important;
      margin: 0 !important;
      transform: rotate(90deg);
      transform-origin: center center;
      page-break-after: auto !important;
      page-break-inside: auto !important;
    }
  ` : ''

  iframeDoc.open()
  iframeDoc.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>打印列表</title>
      ${styles}
      <style>
        body { margin: 0; padding: 0; background: #fff; }
        /* 每页独立分页，避免外层 flex 在混合方向时产生定位偏差 */
        .print-wrapper { display: block; padding: 0; }
        
        /* 竖向页面样式 */
        .print-content { 
          box-shadow: none !important; 
          max-width: none !important;
          width: 210mm !important;
          height: 297mm !important;
          min-height: 297mm !important;
          max-height: 297mm !important;
          padding: 10mm !important;
          margin: 0 auto !important;
          page-break-after: always;
          page-break-inside: avoid;
          box-sizing: border-box;
          overflow: hidden;
        }
        
        /* 横向页面样式 */
        .print-content.landscape-content {
          width: 297mm !important;
          height: 210mm !important;
          min-height: 210mm !important;
          max-height: 210mm !important;
        }
        
        .print-content:last-child {
          page-break-after: avoid;
        }
        
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          
          @page {
            size: A4 ${pageOrientation};
            margin: 0;
          }
          
          .print-content {
            width: ${pageOrientation === 'portrait' ? '210mm' : '297mm'} !important;
            height: ${pageOrientation === 'portrait' ? '297mm' : '210mm'} !important;
            min-height: ${pageOrientation === 'portrait' ? '297mm' : '210mm'} !important;
            max-height: ${pageOrientation === 'portrait' ? '297mm' : '210mm'} !important;
          }
          
          ${landscapeRotateStyle}
        }
      </style>
    </head>
    <body>
      ${printContentClone.outerHTML}
    </body>
    </html>
  `)
  iframeDoc.close()

  // 等待iframe加载完成后打印
  iframe.onload = () => {
    setTimeout(() => {
      iframe.contentWindow?.print()
      setTimeout(() => {
        document.body.removeChild(iframe)
      }, 1000)
    }, 100)
  }
}

// 导出Excel功能（使用ExcelJS支持完整样式）
const handleExportExcel = async () => {
  if (!printData.value || printData.value.length === 0) {
    ElMessage.warning('暂无数据可导出')
    return
  }

  try {
    const workbook = new ExcelJS.Workbook()
    
    // 为每页创建一个工作表
    for (let pageIndex = 0; pageIndex < printData.value.length; pageIndex++) {
      const pageData = printData.value[pageIndex]
      
      // 工作表名称
      let sheetName: string
      if (printData.value.length === 1) {
        sheetName = '打印列表'
      } else {
        const dataIndex = pageData.dataIndex
        const pageInData = pageData.pageIndexInData + 1
        const totalInData = pageData.totalPagesInData
        
        if (totalInData > 1) {
          sheetName = `数据${dataIndex + 1}_第${pageInData}页`
        } else {
          sheetName = `数据${dataIndex + 1}`
        }
      }
      
      const worksheet = workbook.addWorksheet(sheetName)
      
      // 计算实际需要的列数 - 完全基于表格数据
      // 表格列数 = 4个固定列（序号、楼号楼层、轴线号、纵向标题）+ 横向标题数量
      const tableDataCols = 4 + (pageData.tableInfo?.horizontalHeaders?.length || 0)
      
      let currentRow = 1
      
      // 页眉信息 - 使用表格实际列数
      const headerRow = worksheet.getRow(currentRow++)
      headerRow.getCell(1).value = formatDateCode(pageData.implementDate)
      headerRow.getCell(tableDataCols).value = `实施日期：${formatDate(pageData.implementDate)}`
      headerRow.font = { size: 11 }
      
      // 空行
      currentRow++
      
      // 公司标题
      const companyRow1 = worksheet.getRow(currentRow++)
      companyRow1.getCell(1).value = '建研院检测中心有限公司'
      companyRow1.font = { bold: true, size: 16 }
      companyRow1.alignment = { horizontal: 'center', vertical: 'middle' }
      
      const companyRow2 = worksheet.getRow(currentRow++)
      companyRow2.getCell(1).value = '国家建筑工程质量检验检测中心'
      companyRow2.font = { bold: true, size: 14 }
      companyRow2.alignment = { horizontal: 'center', vertical: 'middle' }
      
      const projectRow = worksheet.getRow(currentRow++)
      projectRow.getCell(1).value = pageData.projectName
      projectRow.font = { bold: true, size: 16 }
      projectRow.alignment = { horizontal: 'center', vertical: 'middle' }
      
      const codeRow = worksheet.getRow(currentRow++)
      codeRow.getCell(1).value = `(${pageData.controlNumber})`
      codeRow.font = { bold: true, size: 14 }
      codeRow.alignment = { horizontal: 'center', vertical: 'middle' }
      
      // 空行
      currentRow++
      
      // 基本信息行 - 使用合并单元格确保不被挤压
      const infoRow = worksheet.getRow(currentRow++)
      
      // 计算每个信息项占用的列数
      const colsPerInfo = Math.floor(tableDataCols / 4)
      
      // 记录编号
      infoRow.getCell(1).value = `记录编号：${pageData.recordNumber}`
      if (colsPerInfo > 1) {
        worksheet.mergeCells(currentRow - 1, 1, currentRow - 1, colsPerInfo)
      }
      
      // 设备编号
      if (tableDataCols >= 2) {
        const startCol = colsPerInfo + 1
        infoRow.getCell(startCol).value = `设备编号：${pageData.deviceNumber}`
        if (colsPerInfo > 1 && startCol + colsPerInfo - 1 <= tableDataCols) {
          worksheet.mergeCells(currentRow - 1, startCol, currentRow - 1, Math.min(startCol + colsPerInfo - 1, tableDataCols))
        }
      }
      
      // 环境温度
      if (tableDataCols >= 3) {
        const startCol = colsPerInfo * 2 + 1
        infoRow.getCell(startCol).value = `环境温度${pageData.temperature}℃`
        if (colsPerInfo > 1 && startCol + colsPerInfo - 1 <= tableDataCols) {
          worksheet.mergeCells(currentRow - 1, startCol, currentRow - 1, Math.min(startCol + colsPerInfo - 1, tableDataCols))
        }
      }
      
      // 页码信息
      if (tableDataCols >= 4) {
        const startCol = colsPerInfo * 3 + 1
        infoRow.getCell(startCol).value = `共${pageData.totalPagesInData}页第${pageData.pageIndexInData + 1}页`
        if (startCol + colsPerInfo - 1 <= tableDataCols) {
          worksheet.mergeCells(currentRow - 1, startCol, currentRow - 1, tableDataCols)
        }
      }
      
      infoRow.font = { size: 11 }
      infoRow.alignment = { horizontal: 'left', vertical: 'middle' }
      
      // 特殊属性（只在首页显示；检测对象为"默认"的项不导出）
      const exportSpecialAttributes = visibleSpecialAttributes(pageData)
      if (pageData.isFirstPage && exportSpecialAttributes.length > 0) {
        currentRow++ // 空行
        
        // 每行显示3个特殊属性，按行分组
        const attrsPerRow = 3
        const attrRows = Math.ceil(exportSpecialAttributes.length / attrsPerRow)
        const attrStartRow = currentRow
        
        for (let rowIndex = 0; rowIndex < attrRows; rowIndex++) {
          const attrRow = worksheet.getRow(currentRow++)
          const startIndex = rowIndex * attrsPerRow
          const endIndex = Math.min(startIndex + attrsPerRow, exportSpecialAttributes.length)
          
          // 在每行中分布3个属性，使用合适的列间距
          for (let i = startIndex; i < endIndex; i++) {
            const attr = exportSpecialAttributes[i]
            const colIndex = ((i - startIndex) * Math.floor(tableDataCols / attrsPerRow)) + 1
            attrRow.getCell(colIndex).value = `${attr.title}：${attr.value}`
          }
          
          attrRow.font = { size: 11 }
          attrRow.alignment = { horizontal: 'left', vertical: 'middle' }
        }
        
        // 只为特殊属性区域的外围添加边框
        const attrEndRow = currentRow - 1
        for (let row = attrStartRow; row <= attrEndRow; row++) {
          for (let col = 1; col <= tableDataCols; col++) {
            const cell = worksheet.getCell(row, col)
            const border: any = {}
            
            // 只在外围添加边框
            if (row === attrStartRow) border.top = { style: 'thin' }
            if (row === attrEndRow) border.bottom = { style: 'thin' }
            if (col === 1) border.left = { style: 'thin' }
            if (col === tableDataCols) border.right = { style: 'thin' }
            
            if (Object.keys(border).length > 0) {
              cell.border = border
            }
          }
        }
      }
      
      // 续页标识
      if (!pageData.isFirstPage) {
        const continueRow = worksheet.getRow(currentRow++)
        continueRow.getCell(1).value = '（续上页）'
        continueRow.font = { size: 11 }
        continueRow.alignment = { horizontal: 'center' }
      }
      
      // 空行
      currentRow++
      
      // 表格数据
      if (pageData.pageRows && pageData.pageRows.length > 0) {
        const tableStartRow = currentRow
        
        // 表头 - 分区名称行
        const sectionHeaderRow = worksheet.getRow(currentRow++)
        sectionHeaderRow.getCell(1).value = '序号'
        sectionHeaderRow.getCell(2).value = '楼号楼层'
        sectionHeaderRow.getCell(3).value = '轴线号'
        sectionHeaderRow.getCell(4).value = ''
        
        let colIndex = 5
        pageData.sections.forEach(section => {
          sectionHeaderRow.getCell(colIndex).value = section.name
          // 合并分区标题单元格
          if (section.columnCount > 1) {
            worksheet.mergeCells(currentRow - 1, colIndex, currentRow - 1, colIndex + section.columnCount - 1)
          }
          colIndex += section.columnCount
        })
        
        // 设置表头样式 - 只为实际列设置边框
        for (let col = 1; col <= tableDataCols; col++) {
          const cell = sectionHeaderRow.getCell(col)
          cell.font = { bold: true, size: 12 }
          cell.alignment = { horizontal: 'center', vertical: 'middle' }
          cell.border = {
            top: { style: 'thin' },
            bottom: { style: 'thin' },
            left: { style: 'thin' },
            right: { style: 'thin' }
          }
        }
        
        // 表头 - 横向标题行
        if (pageData.tableInfo?.horizontalHeaders) {
          const headerRow = worksheet.getRow(currentRow++)
          headerRow.getCell(1).value = ''
          headerRow.getCell(2).value = ''
          headerRow.getCell(3).value = ''
          headerRow.getCell(4).value = ''
          
          pageData.tableInfo.horizontalHeaders.forEach((header, index) => {
            headerRow.getCell(5 + index).value = header
          })
          
          // 设置表头样式 - 只为实际列设置边框
          for (let col = 1; col <= tableDataCols; col++) {
            const cell = headerRow.getCell(col)
            cell.font = { bold: true, size: 11 }
            cell.alignment = { horizontal: 'center', vertical: 'middle' }
            cell.border = {
              top: { style: 'thin' },
              bottom: { style: 'thin' },
              left: { style: 'thin' },
              right: { style: 'thin' }
            }
          }
        }
        
        // 数据行
        let rowIndex = 0

        while (rowIndex < pageData.pageRows.length) {
          const firstRow = pageData.pageRows[rowIndex]
          const groupRows: FlatTableRow[] = []

          while (
            rowIndex < pageData.pageRows.length &&
            pageData.pageRows[rowIndex].mainIndex === firstRow.mainIndex
          ) {
            groupRows.push(pageData.pageRows[rowIndex])
            rowIndex++
          }

          const singleAttrs = getMainSingleAttrsList(pageData, firstRow.mainIndex)
          const seqStartRow = currentRow

          if (singleAttrs.length > 0) {
            const singleAttrRowNumber = currentRow++
            const singleAttrRow = worksheet.getRow(singleAttrRowNumber)
            singleAttrRow.getCell(1).value = firstRow.mainIndex + 1
            singleAttrRow.getCell(2).value = `单条字段  ${formatSingleAttrsText(singleAttrs)}`
            worksheet.mergeCells(singleAttrRowNumber, 2, singleAttrRowNumber, tableDataCols)
            singleAttrRow.height = 22

            for (let col = 1; col <= tableDataCols; col++) {
              const cell = singleAttrRow.getCell(col)
              cell.font = { size: 10 }
              cell.alignment = { horizontal: col === 1 ? 'center' : 'left', vertical: 'middle', wrapText: true }
              cell.border = {
                top: { style: 'thin' },
                bottom: { style: 'thin' },
                left: { style: 'thin' },
                right: { style: 'thin' }
              }
            }
          }

          const dataStartRow = currentRow

          groupRows.forEach((row, groupRowIndex) => {
            const dataRow = worksheet.getRow(currentRow++)

            // 固定列：序号会在有单条字段时从单条字段行开始合并
            if (groupRowIndex === 0) {
              if (singleAttrs.length === 0) {
                dataRow.getCell(1).value = row.mainIndex + 1
              }
              dataRow.getCell(2).value = row.buildingFloor
              dataRow.getCell(3).value = row.axisNumber
            } else {
              dataRow.getCell(1).value = ''
              dataRow.getCell(2).value = ''
              dataRow.getCell(3).value = ''
            }

            // 纵向标题
            dataRow.getCell(4).value = row.verticalHeader

            // 数据单元格
            row.cells.forEach((cell, index) => {
              dataRow.getCell(5 + index).value = cell || ''
            })

            // 设置数据行样式 - 只为实际列设置边框
            for (let col = 1; col <= tableDataCols; col++) {
              const cell = dataRow.getCell(col)
              cell.font = { size: 11 }
              cell.alignment = { horizontal: 'center', vertical: 'middle' }
              cell.border = {
                top: { style: 'thin' },
                bottom: { style: 'thin' },
                left: { style: 'thin' },
                right: { style: 'thin' }
              }
            }
          })

          const dataEndRow = currentRow - 1
          const seqEndRow = dataEndRow

          if (seqEndRow > seqStartRow) {
            worksheet.mergeCells(seqStartRow, 1, seqEndRow, 1)
          }

          if (groupRows.length > 1) {
            worksheet.mergeCells(dataStartRow, 2, dataEndRow, 2)
            worksheet.mergeCells(dataStartRow, 3, dataEndRow, 3)
          }
        }
      }
      
      // 空行
      currentRow++

      // 页脚签名区 - 使用与页头相同的间隔分布
      const footerRow = worksheet.getRow(currentRow++)

      // 计算页脚项目数量（检验、计算（可选）、校核、记录、检验日期）
      const footerItems: string[] = [`检验：${pageData.inspector}`]
      if (pageData.showCalculator) {
        footerItems.push(`计算：${pageData.calculator}`)
      }
      footerItems.push(`校核：${pageData.reviewer}`)
      footerItems.push(`记录：${pageData.recorder}`)
      footerItems.push(`检验日期：${formatDate(pageData.inspectionDate)}`)

      // 按比例分配列位置，与页头保持一致
      const colsPerFooterItem = Math.floor(tableDataCols / footerItems.length)
      footerItems.forEach((item, index) => {
        const startCol = index * colsPerFooterItem + 1
        footerRow.getCell(startCol).value = item
        // 合并单元格使内容有足够空间
        const endCol = index === footerItems.length - 1 ? tableDataCols : (index + 1) * colsPerFooterItem
        if (endCol > startCol) {
          worksheet.mergeCells(currentRow - 1, startCol, currentRow - 1, endCol)
        }
      })

      footerRow.font = { size: 11 }
      
      // 设置列宽 - 严格按照表格数据列数设置
      worksheet.getColumn(1).width = 8   // 序号
      worksheet.getColumn(2).width = 15  // 楼号楼层
      worksheet.getColumn(3).width = 10  // 轴线号
      worksheet.getColumn(4).width = 12  // 纵向标题
      
      // 为数据列设置宽度
      if (pageData.tableInfo?.horizontalHeaders) {
        pageData.tableInfo.horizontalHeaders.forEach((_, index) => {
          worksheet.getColumn(5 + index).width = 12
        })
      }
      
      // 合并公司标题单元格 - 使用表格实际列数
      worksheet.mergeCells(3, 1, 3, tableDataCols)
      worksheet.mergeCells(4, 1, 4, tableDataCols)
      worksheet.mergeCells(5, 1, 5, tableDataCols)
      worksheet.mergeCells(6, 1, 6, tableDataCols)
      
      // 为合并后的标题单元格设置居中样式
      worksheet.getCell(3, 1).alignment = { horizontal: 'center', vertical: 'middle' }
      worksheet.getCell(4, 1).alignment = { horizontal: 'center', vertical: 'middle' }
      worksheet.getCell(5, 1).alignment = { horizontal: 'center', vertical: 'middle' }
      worksheet.getCell(6, 1).alignment = { horizontal: 'center', vertical: 'middle' }
    }
    
    // 生成文件名
    const firstPage = printData.value[0]
    const fileName = `${firstPage.projectName || '打印列表'}_${formatDateCode(firstPage.implementDate) || new Date().toISOString().slice(0, 10).replace(/-/g, '')}.xlsx`
    
    // 导出文件
    const buffer = await workbook.xlsx.writeBuffer()
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
    saveAs(blob, fileName)
    
    ElMessage.success('Excel导出成功')
  } catch (error) {
    console.error('Excel导出失败:', error)
    ElMessage.error('Excel导出失败，请重试')
  }
}

onMounted(() => {
  loadPrintData()
})

watch(
  () => [route.path, route.query.cacheKey, route.query.id],
  () => {
    loadPrintData()
  }
)

return (_ctx: any,_cache: any) => {
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_tooltip = _resolveComponent("el-tooltip")!
  const _component_el_skeleton = _resolveComponent("el-skeleton")!
  const _component_el_empty = _resolveComponent("el-empty")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createVNode(_component_el_button, {
        type: "primary",
        icon: _unref(ArrowLeft),
        onClick: goBack
      }, {
        default: _withCtx(() => _cache[0] || (_cache[0] = [
          _createTextVNode("返回")
        ])),
        _: 1
      }, 8, ["icon"]),
      _createVNode(_component_el_button, {
        type: "success",
        icon: _unref(Printer),
        onClick: handlePrint
      }, {
        default: _withCtx(() => _cache[1] || (_cache[1] = [
          _createTextVNode("打印")
        ])),
        _: 1
      }, 8, ["icon"]),
      _createVNode(_component_el_button, {
        type: "warning",
        icon: _unref(Download),
        onClick: handleExportExcel
      }, {
        default: _withCtx(() => _cache[2] || (_cache[2] = [
          _createTextVNode("导出Excel")
        ])),
        _: 1
      }, 8, ["icon"]),
      _createVNode(_component_el_tooltip, {
        content: "刷新打印详情",
        placement: "left",
        class: "page-refresh-action"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_el_button, {
            type: "primary",
            circle: "",
            icon: _unref(Refresh),
            loading: loading.value,
            onClick: handleRefreshPrintData
          }, null, 8, ["icon", "loading"])
        ]),
        _: 1
      })
    ]),
    (loading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
          _createVNode(_component_el_skeleton, {
            rows: 10,
            animated: ""
          })
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", {
      class: _normalizeClass(["print-wrapper", { 'print-wrapper--preparing': loading.value }]),
      ref_key: "printContentRef",
      ref: printContentRef
    }, [
      (printData.value && printData.value.length > 0)
        ? (_openBlock(true), _createElementBlock(_Fragment, { key: 0 }, _renderList(printData.value, (item, pageIndex) => {
            return (_openBlock(), _createElementBlock("div", {
              key: pageIndex,
              class: _normalizeClass(["print-content", { 'landscape-content': item.isLandscape }])
            }, [
              _createElementVNode("div", _hoisted_4, [
                _createElementVNode("div", _hoisted_5, [
                  _createElementVNode("span", _hoisted_6, _toDisplayString(formatDateCode(item.implementDate)), 1),
                  _createElementVNode("span", _hoisted_7, "实施日期：" + _toDisplayString(formatDate(item.implementDate)), 1)
                ]),
                _createElementVNode("div", _hoisted_8, [
                  _cache[3] || (_cache[3] = _createElementVNode("div", { class: "company-name" }, "建研院检测中心有限公司", -1)),
                  _cache[4] || (_cache[4] = _createElementVNode("div", { class: "company-sub" }, "国家建筑工程质量检验检测中心", -1)),
                  _createElementVNode("div", _hoisted_9, _toDisplayString(item.projectName), 1),
                  _createElementVNode("div", _hoisted_10, "(" + _toDisplayString(item.controlNumber) + ")", 1)
                ]),
                _createElementVNode("div", _hoisted_11, [
                  _createElementVNode("div", _hoisted_12, [
                    _cache[5] || (_cache[5] = _createElementVNode("span", { class: "info-label" }, "记录编号：", -1)),
                    _createElementVNode("span", {
                      class: _normalizeClass(["info-value", { 'empty-value': !hasDisplayValue(item.recordNumber) }])
                    }, _toDisplayString(item.recordNumber), 3)
                  ]),
                  _createElementVNode("div", _hoisted_13, [
                    _cache[6] || (_cache[6] = _createElementVNode("span", { class: "info-label" }, "设备编号：", -1)),
                    _createElementVNode("span", {
                      class: _normalizeClass(["info-value", { 'empty-value': !hasDisplayValue(item.deviceNumber) }])
                    }, _toDisplayString(item.deviceNumber), 3)
                  ]),
                  _createElementVNode("div", _hoisted_14, [
                    _cache[7] || (_cache[7] = _createElementVNode("span", { class: "info-label" }, "环境温度", -1)),
                    _createElementVNode("span", {
                      class: _normalizeClass(["info-value", { 'empty-value': !hasDisplayValue(item.temperature) }])
                    }, _toDisplayString(item.temperature), 3),
                    _cache[8] || (_cache[8] = _createElementVNode("span", { class: "info-label" }, "℃", -1))
                  ]),
                  _createElementVNode("div", _hoisted_15, [
                    _cache[9] || (_cache[9] = _createElementVNode("span", { class: "info-label" }, "共", -1)),
                    _createElementVNode("span", _hoisted_16, _toDisplayString(item.totalPagesInData), 1),
                    _cache[10] || (_cache[10] = _createElementVNode("span", { class: "info-label" }, "页第", -1)),
                    _createElementVNode("span", _hoisted_17, _toDisplayString(item.pageIndexInData + 1), 1),
                    _cache[11] || (_cache[11] = _createElementVNode("span", { class: "info-label" }, "页", -1))
                  ])
                ]),
                (item.isFirstPage && visibleSpecialAttributes(item).length > 0)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_18, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleSpecialAttributes(item), (attr, attrIndex) => {
                        return (_openBlock(), _createElementBlock("div", {
                          key: attrIndex,
                          class: "special-attr-item"
                        }, [
                          _createElementVNode("span", _hoisted_19, _toDisplayString(attr.title), 1),
                          _createElementVNode("span", {
                            class: _normalizeClass(["attr-value", { 'empty-value': !hasDisplayValue(attr.value) }])
                          }, _toDisplayString(attr.value), 3)
                        ]))
                      }), 128))
                    ]))
                  : _createCommentVNode("", true),
                (!item.isFirstPage)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_20, " （续上页） "))
                  : _createCommentVNode("", true),
                (item.pageRows && item.pageRows.length > 0)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_21, [
                      _createElementVNode("table", _hoisted_22, [
                        _createElementVNode("thead", null, [
                          (item.tableInfo && item.tableInfo.horizontalHeaders)
                            ? (_openBlock(), _createElementBlock("tr", _hoisted_23, [
                                _cache[12] || (_cache[12] = _createElementVNode("th", { class: "col-seq" }, [
                                  _createTextVNode("序"),
                                  _createElementVNode("br"),
                                  _createTextVNode("号")
                                ], -1)),
                                _cache[13] || (_cache[13] = _createElementVNode("th", { class: "col-building" }, [
                                  _createTextVNode("楼号"),
                                  _createElementVNode("br"),
                                  _createTextVNode("楼层")
                                ], -1)),
                                _cache[14] || (_cache[14] = _createElementVNode("th", { class: "col-axis" }, "轴线号", -1)),
                                _cache[15] || (_cache[15] = _createElementVNode("th", { class: "col-vertical" }, null, -1)),
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(item.tableInfo.horizontalHeaders, (title, hIndex) => {
                                  return (_openBlock(), _createElementBlock("th", {
                                    key: hIndex,
                                    class: _normalizeClass(hasDividerLine(item.tableInfo, hIndex) ? 'divider-column' : ''),
                                    width: 120
                                  }, _toDisplayString(getTransformedTitle(title, item.tableInfo, item.pageRows[0])), 3))
                                }), 128))
                              ]))
                            : _createCommentVNode("", true)
                        ]),
                        _createElementVNode("tbody", null, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(item.pageRows, (row, rowIndex) => {
                            return (_openBlock(), _createElementBlock(_Fragment, { key: rowIndex }, [
                              (shouldRenderSingleAttrsRow(item, row, rowIndex))
                                ? (_openBlock(), _createElementBlock("tr", {
                                    key: 0,
                                    class: _normalizeClass(["single-attrs-row", { 'main-row-start': rowIndex > 0 }])
                                  }, [
                                    _createElementVNode("td", {
                                      class: "col-seq",
                                      rowspan: getSeqRowspanInPage(item, rowIndex)
                                    }, _toDisplayString(row.mainIndex + 1), 9, _hoisted_24),
                                    _createElementVNode("td", {
                                      class: "single-attrs-cell",
                                      colspan: getTableColumnCount(item) - 1
                                    }, [
                                      _createElementVNode("div", _hoisted_26, [
                                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(getMainSingleAttrsList(item, row.mainIndex), (attr, attrIndex) => {
                                          return (_openBlock(), _createElementBlock("span", {
                                            key: attrIndex,
                                            class: "single-attrs-item"
                                          }, [
                                            _createElementVNode("span", _hoisted_27, _toDisplayString(attr.title), 1),
                                            _createElementVNode("span", {
                                              class: _normalizeClass(["single-attrs-value", { 'empty-value': !hasDisplayValue(attr.value) }])
                                            }, _toDisplayString(attr.value), 3)
                                          ]))
                                        }), 128))
                                      ])
                                    ], 8, _hoisted_25)
                                  ], 2))
                                : _createCommentVNode("", true),
                              _createElementVNode("tr", {
                                class: _normalizeClass({ 'main-row-start': !hasMainSingleAttrs(item, row.mainIndex) && row.isFirstSubRow && rowIndex > 0 && (rowIndex === 0 || item.pageRows[rowIndex - 1]?.mainIndex !== row.mainIndex) }),
                                style: _normalizeStyle(getRowStyle(item.rowExtraHeights?.[rowIndex] || 0, item.isLandscape))
                              }, [
                                (shouldRenderSeqCell(item, row, rowIndex))
                                  ? (_openBlock(), _createElementBlock("td", {
                                      key: 0,
                                      class: "col-seq",
                                      rowspan: getSeqRowspanInPage(item, rowIndex),
                                      style: _normalizeStyle(getCellStyle(item.rowExtraHeights?.[rowIndex] || 0, item.isLandscape))
                                    }, _toDisplayString(row.mainIndex + 1), 13, _hoisted_28))
                                  : _createCommentVNode("", true),
                                (row.isFirstSubRow && isFirstRowOfMainInPage(item.pageRows, rowIndex))
                                  ? (_openBlock(), _createElementBlock("td", {
                                      key: 1,
                                      class: "col-building",
                                      rowspan: getRowspanInPage(item.pageRows, rowIndex),
                                      style: _normalizeStyle(getCellStyle(item.rowExtraHeights?.[rowIndex] || 0, item.isLandscape))
                                    }, _toDisplayString(row.buildingFloor), 13, _hoisted_29))
                                  : _createCommentVNode("", true),
                                (row.isFirstSubRow && isFirstRowOfMainInPage(item.pageRows, rowIndex))
                                  ? (_openBlock(), _createElementBlock("td", {
                                      key: 2,
                                      class: "col-axis fixed-col-end",
                                      rowspan: getRowspanInPage(item.pageRows, rowIndex),
                                      style: _normalizeStyle(getCellStyle(item.rowExtraHeights?.[rowIndex] || 0, item.isLandscape))
                                    }, _toDisplayString(row.axisNumber), 13, _hoisted_30))
                                  : _createCommentVNode("", true),
                                _createElementVNode("td", {
                                  class: "col-vertical",
                                  style: _normalizeStyle(getCellStyle(item.rowExtraHeights?.[rowIndex] || 0, item.isLandscape))
                                }, _toDisplayString(row.verticalHeader), 5),
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(row.cells, (cell, cellIndex) => {
                                  return (_openBlock(), _createElementBlock("td", {
                                    key: cellIndex,
                                    class: _normalizeClass(getSectionClassByIndex(item.sections, cellIndex)),
                                    style: _normalizeStyle(getCellStyle(item.rowExtraHeights?.[rowIndex] || 0, item.isLandscape))
                                  }, _toDisplayString(cell), 7))
                                }), 128))
                              ], 6)
                            ], 64))
                          }), 128))
                        ])
                      ])
                    ]))
                  : _createCommentVNode("", true)
              ]),
              _createElementVNode("div", _hoisted_31, [
                _createElementVNode("div", _hoisted_32, [
                  _cache[16] || (_cache[16] = _createElementVNode("span", { class: "footer-label" }, "检验：", -1)),
                  _createElementVNode("span", {
                    class: _normalizeClass(["footer-value", { 'empty-value': !hasDisplayValue(item.inspector) }])
                  }, _toDisplayString(item.inspector), 3)
                ]),
                (item.showCalculator)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_33, [
                      _cache[17] || (_cache[17] = _createElementVNode("span", { class: "footer-label" }, "计算：", -1)),
                      _createElementVNode("span", {
                        class: _normalizeClass(["footer-value", { 'empty-value': !hasDisplayValue(item.calculator) }])
                      }, _toDisplayString(item.calculator), 3)
                    ]))
                  : _createCommentVNode("", true),
                _createElementVNode("div", _hoisted_34, [
                  _cache[18] || (_cache[18] = _createElementVNode("span", { class: "footer-label" }, "校核：", -1)),
                  _createElementVNode("span", {
                    class: _normalizeClass(["footer-value", { 'empty-value': !hasDisplayValue(item.reviewer) }])
                  }, _toDisplayString(item.reviewer), 3)
                ]),
                _createElementVNode("div", _hoisted_35, [
                  _cache[19] || (_cache[19] = _createElementVNode("span", { class: "footer-label" }, "记录：", -1)),
                  _createElementVNode("span", {
                    class: _normalizeClass(["footer-value", { 'empty-value': !hasDisplayValue(item.recorder) }])
                  }, _toDisplayString(item.recorder), 3)
                ]),
                _createElementVNode("div", _hoisted_36, [
                  _cache[20] || (_cache[20] = _createElementVNode("span", { class: "footer-label" }, "检验日期：", -1)),
                  _createElementVNode("span", {
                    class: _normalizeClass(["footer-value", { 'empty-value': !hasDisplayValue(item.inspectionDate) }])
                  }, _toDisplayString(formatDate(item.inspectionDate)), 3)
                ])
              ])
            ], 2))
          }), 128))
        : _createCommentVNode("", true),
      (!loading.value && (!printData.value || printData.value.length === 0))
        ? (_openBlock(), _createBlock(_component_el_empty, {
            key: 1,
            description: "暂无打印数据"
          }))
        : _createCommentVNode("", true)
    ], 2)
  ]))
}
}

})